import { useCallback } from 'preact/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getPortalTerrain3dCards } from '../../lib/portal-detail-api.js';
import { resolvePortalMorphologyLabel } from '../../lib/portal-detail-card-contract.js';
import {
  resolveInsightMorphologyType,
  resolveInsightMorphologyVariant,
} from '../../lib/portal-insight-helpers.js';
import ParcelMorphologyThreeCard from '../parcelMorphology/ParcelMorphologyThreeCard/ParcelMorphologyThreeCard.jsx';
import { DetailPanelIcon } from './DetailTopPanelIcons.jsx';

export default function DetailInsightMorphologyMetricCard({
  summary,
  onOpenSlopeTab,
  snapshotId = null,
  embedMesh = false,
  meshEnabled = true,
  onExpandMesh = null,
  onMeshReady = null,
}) {
  const morphologyLabel = resolvePortalMorphologyLabel(summary);
  const morphologyType = resolveInsightMorphologyType(summary);
  const variant = resolveInsightMorphologyVariant(morphologyType);
  const variantClass = variant === 'alert' ? ' pp-detail-insight-metric-card--alert' : '';

  const meshQuery = useQuery({
    queryKey: ['portal-terrain-3d-cards', snapshotId],
    queryFn: () => getPortalTerrain3dCards(snapshotId),
    select: (data) => data?.morphology ?? null,
    enabled: Boolean(embedMesh && snapshotId && meshEnabled),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const handleReady = useCallback(() => {
    onMeshReady?.();
  }, [onMeshReady]);

  if (!embedMesh) {
    return (
      <button
        type="button"
        role="listitem"
        className={`pp-detail-insight-metric-card pp-detail-insight-morphology-metric-card pp-detail-insight-metric-card--clickable${variantClass}`}
        onClick={onOpenSlopeTab}
        title="Morfoloji 3D kartını harita alanında aç"
        aria-label={`Morfolojik tip ${morphologyLabel}. Morfoloji 3D kartını harita alanında aç.`}
      >
        <div className="pp-detail-insight-metric-icon" aria-hidden="true">
          <DetailPanelIcon name="slope" size={17} />
        </div>
        <div className="pp-detail-insight-metric-content">
          <p className="pp-detail-insight-metric-title">Morfolojik Tip</p>
          <div className="pp-detail-insight-metric-value">{morphologyLabel}</div>
        </div>
      </button>
    );
  }

  const waitingGate = embedMesh && !meshEnabled;
  const meshLoading = waitingGate || (meshQuery.isLoading && !meshQuery.data);
  const meshError = meshQuery.isError
    ? (meshQuery.error?.message || '3D morfoloji yüklenemedi')
    : null;
  const canExpand = Boolean(onExpandMesh && snapshotId);
  const showScene = Boolean(meshQuery.data) && !meshError;

  return (
    <div
      role="listitem"
      className={`pp-detail-insight-metric-card pp-detail-insight-morphology-metric-card pp-detail-insight-morphology-metric-card--stacked${variantClass}`}
      aria-label={`Morfolojik tip ${morphologyLabel}.`}
    >
      <div className="pp-detail-insight-metric-card__head-row">
        <button
          type="button"
          className="pp-detail-insight-morphology-metric-card__head-btn"
          onClick={onOpenSlopeTab}
          title="Morfoloji 3D kartını harita alanında aç"
          aria-label={`Morfolojik tip ${morphologyLabel}. Morfoloji 3D kartını harita alanında aç.`}
        >
          <div className="pp-detail-insight-metric-icon" aria-hidden="true">
            <DetailPanelIcon name="slope" size={17} />
          </div>
          <div className="pp-detail-insight-metric-content">
            <p className="pp-detail-insight-metric-title">Morfolojik Tip</p>
            <div className="pp-detail-insight-metric-value">{morphologyLabel}</div>
          </div>
        </button>
        {canExpand ? (
          <button
            type="button"
            className="pp-detail-insight-metric-card__expand-btn"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onExpandMesh('morphology');
            }}
            title="Haritada tam kartı aç"
            aria-label="Morfoloji 3D kartını harita alanında aç"
          >
            <i className="fas fa-expand" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="pp-detail-insight-morphology-metric-card__mesh">
        {meshLoading && !showScene ? (
          <div className="pp-detail-insight-morphology-metric-card__mesh-skel" aria-busy="true" />
        ) : null}
        {meshError ? (
          <div className="pp-detail-insight-morphology-metric-card__mesh-err" role="status">
            {meshError}
          </div>
        ) : null}
        {showScene ? (
          <ParcelMorphologyThreeCard
            data={meshQuery.data}
            compact
            onReady={handleReady}
          />
        ) : null}
        {!meshLoading && !meshError && !meshQuery.data ? (
          <div className="pp-detail-insight-morphology-metric-card__mesh-err" role="status">
            Buffer mesh yok
          </div>
        ) : null}
      </div>
    </div>
  );
}
